import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import type { AxiosError } from 'axios';
import useTranslation from 'next-translate/useTranslation';

import { validateVoucher, VoucherNotValidError } from '~/services/wallet/wallet.service.api';

import type { VoucherResponseDto } from '../types/voucher.types';
import { VoucherEntryType } from '../types/voucher.types';
import { ContestVoucherValidationQueryKey } from '../constants/query';

type UseContestVoucherValidationParams = {
  selectedVoucher?: VoucherResponseDto | null;
  contestId?: string;
  league?: string;
  contestType?: string;
  entryAmountInDollars: number;
};

type UseContestVoucherValidationReturn = {
  voucherError: string | null;
  isValidatingVoucher: boolean;
};

type ValidationErrorPayload = { message?: string } | { data?: { reason?: string } };

const extractVoucherErrorMessage = (error: unknown, fallback: string): string => {
  // A 200 with `isValid: false` is normalized into VoucherNotValidError,
  // carrying the backend reason directly.
  if (error instanceof VoucherNotValidError) {
    return error.reason;
  }
  const axiosError = error as AxiosError<ValidationErrorPayload>;
  const errorData = axiosError?.response?.data;
  const messageFromError =
    errorData && 'message' in errorData ? errorData.message : undefined;
  const reasonFromData =
    errorData && 'data' in errorData ? errorData.data?.reason : undefined;
  return messageFromError ?? reasonFromData ?? fallback;
};

/**
 * Validates a selected voucher against the contest the user is about to enter.
 * Runs proactively via `useQuery` so the result is cached by `{voucherId, contestId, amount, league, contestType}`
 * and re-fires when any of those change.
 *
 * Toasts are emitted from inside the `queryFn`, which React Query dedupes by query key, so multiple
 * subscribers or StrictMode double-mounts share a single fetch and therefore a single notification.
 */
export function useContestVoucherValidation({
  selectedVoucher,
  contestId,
  league,
  contestType,
  entryAmountInDollars,
}: UseContestVoucherValidationParams): UseContestVoucherValidationReturn {
  const { t } = useTranslation('contest');
  const voucherId = selectedVoucher?.voucherId;
  const fallbackMessage = t('common.enterContestError.voucherInvalid');
  const toastTitle = t('common.enterContestError.voucherValidation.title');

  // eslint-disable-next-line local-rules/require-query-stale-time
  const query = useQuery({
    queryKey: ContestVoucherValidationQueryKey(
      voucherId,
      contestId,
      entryAmountInDollars,
      league,
      contestType
    ),
    enabled: !!selectedVoucher && !!voucherId && !!contestId && entryAmountInDollars > 0,
    queryFn: async () => {
      try {
        return await validateVoucher({
          voucherId: voucherId as string,
          payload: {
            voucherType: (selectedVoucher as VoucherResponseDto).voucherType,
            amount: entryAmountInDollars,
            entryType: VoucherEntryType.CONTEST,
            contestId,
            ...(league && { league }),
            ...(contestType && { contestType }),
          },
        });
      } catch (error) {
        showNotification({
          title: toastTitle,
          message: extractVoucherErrorMessage(error, fallbackMessage),
          color: 'yellow.5',
          autoClose: 5000,
        });
        throw error;
      }
    },
    retry: false,
  });

  const voucherError = useMemo<string | null>(() => {
    if (!selectedVoucher) return null;
    if (!query.error) return null;
    return extractVoucherErrorMessage(query.error, fallbackMessage);
  }, [selectedVoucher, query.error, fallbackMessage]);

  const isValidatingVoucher = !!selectedVoucher && (query.isLoading || query.isFetching);

  return {
    voucherError,
    isValidatingVoucher,
  };
}

export type { UseContestVoucherValidationParams, UseContestVoucherValidationReturn };
